import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ThumbsUp,
  ThumbsDown,
  RefreshCw,
  Trash2,
  Loader2,
  Eye,
  EyeOff,
  ArrowLeft,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import {
  listarAnotaciones,
  eliminarAnotacion,
  isAnotadorActivo,
  activarAnotador,
} from "../lib/anotador";
import { fmtEUR } from "../lib/storage";

const FILTROS = [
  { id: "todos", label: "Todas" },
  { id: "bueno", label: "Buenas" },
  { id: "malo", label: "Malas" },
];

function formatFecha(v) {
  if (!v) return "";
  try {
    return new Date(v).toLocaleString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(v);
  }
}

export default function AnnotationsAdmin() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtro, setFiltro] = useState("todos");
  const [seleccionada, setSeleccionada] = useState(null);
  const [eliminando, setEliminando] = useState(null);

  useEffect(() => {
    if (!isAnotadorActivo()) {
      toast.error("El modo anotador no está activo");
      navigate("/");
    }
  }, [navigate]);

  const cargar = async () => {
    setLoading(true);
    try {
      const params = filtro === "todos" ? {} : { veredicto: filtro };
      const data = await listarAnotaciones({ ...params, limit: 200 });
      setItems(data?.items ?? []);
    } catch (err) {
      console.error("admin_anotaciones_list_error", err);
      toast.error("Error cargando anotaciones");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargar();
  }, [filtro]); // eslint-disable-line react-hooks/exhaustive-deps

  const stats = useMemo(() => {
    const buenos = items.filter((i) => i.veredicto === "bueno").length;
    const malos = items.filter((i) => i.veredicto === "malo").length;
    return { total: items.length, buenos, malos };
  }, [items]);

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar esta anotación?")) return;
    setEliminando(id);
    try {
      await eliminarAnotacion(id);
      toast.success("Anotación eliminada");
      setItems((prev) => prev.filter((i) => i.id !== id));
      if (seleccionada?.id === id) setSeleccionada(null);
    } catch (err) {
      console.error("admin_anotacion_delete_error", err);
      toast.error("Error eliminando");
    } finally {
      setEliminando(null);
    }
  };

  return (
    <div className="px-10 py-10 max-w-6xl mx-auto" data-testid="annotations-admin">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(-1)}
              className="gap-1"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Volver
            </Button>
            <h1 className="font-display font-bold text-2xl">Anotaciones IA</h1>
          </div>
          <div className="text-sm text-slate-500 mt-1">
            Estos ejemplos se usan como few-shot y para re-rankear candidatas en futuras IA.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={cargar} className="gap-2">
            {loading ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <RefreshCw className="w-3.5 h-3.5" />
            )}
            Recargar
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              activarAnotador(false);
              toast("Modo anotador desactivado");
              navigate("/");
            }}
            className="gap-2"
          >
            <EyeOff className="w-3.5 h-3.5" /> Salir del modo
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="rounded-lg border border-slate-200 bg-white p-3">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">
            Total
          </div>
          <div className="font-display font-bold text-2xl mt-1">
            {stats.total}
          </div>
        </div>
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3">
          <div className="text-[11px] uppercase tracking-widest text-emerald-700 font-bold flex items-center gap-1">
            <ThumbsUp className="w-3 h-3" /> Buenas
          </div>
          <div className="font-display font-bold text-2xl mt-1 text-emerald-700">
            {stats.buenos}
          </div>
        </div>
        <div className="rounded-lg border border-red-200 bg-red-50/40 p-3">
          <div className="text-[11px] uppercase tracking-widest text-red-700 font-bold flex items-center gap-1">
            <ThumbsDown className="w-3 h-3" /> Malas
          </div>
          <div className="font-display font-bold text-2xl mt-1 text-red-700">
            {stats.malos}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-1 mb-4">
        {FILTROS.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFiltro(f.id)}
            className={`px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-widest transition-colors ${
              filtro === f.id
                ? "bg-blue-600 text-white"
                : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-4">
        <div className="space-y-2">
          {loading && items.length === 0 ? (
            <div className="text-sm text-slate-500 px-2 py-6 text-center">
              Cargando…
            </div>
          ) : items.length === 0 ? (
            <div className="text-sm text-slate-500 px-2 py-6 text-center border border-dashed border-slate-300 rounded-lg">
              No hay anotaciones todavía. Marca un presupuesto desde el modo anotador.
            </div>
          ) : (
            items.map((it) => (
              <button
                key={it.id}
                type="button"
                onClick={() => setSeleccionada(it)}
                className={`w-full text-left rounded-lg border p-3 bg-white hover:shadow-sm transition-shadow ${
                  it.veredicto === "bueno"
                    ? "border-emerald-200"
                    : "border-red-200"
                } ${seleccionada?.id === it.id ? "ring-2 ring-blue-300" : ""}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span
                    className={`text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded ${
                      it.veredicto === "bueno"
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-red-100 text-red-700"
                    }`}
                  >
                    {it.veredicto}
                  </span>
                  <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                    {it.fase}
                  </span>
                  {it.tipo_proyecto ? (
                    <span className="text-[10px] uppercase tracking-widest text-slate-500">
                      {it.tipo_proyecto}
                    </span>
                  ) : null}
                  <span className="text-[10px] text-slate-400 ml-auto">
                    {formatFecha(it.created_at)}
                  </span>
                </div>
                <div className="text-sm text-slate-800 line-clamp-2">
                  {it.prompt}
                </div>
                <div className="flex items-center gap-3 mt-1 text-[11px] text-slate-500">
                  <span>{(it.intenciones ?? []).length} intenciones</span>
                  <span>{(it.partidas_finales ?? []).length} partidas</span>
                  {it.coste_total != null ? (
                    <span className="font-bold tabular-nums text-slate-700">
                      {fmtEUR(it.coste_total)}
                    </span>
                  ) : null}
                </div>
                {it.nota ? (
                  <div className="text-[11px] text-slate-600 italic mt-1 line-clamp-2">
                    “{it.nota}”
                  </div>
                ) : null}
              </button>
            ))
          )}
        </div>

        <aside className="rounded-lg border border-slate-200 bg-white p-4 sticky top-4 self-start max-h-[calc(100vh-32px)] overflow-y-auto">
          {seleccionada ? (
            <DetalleAnotacion
              anotacion={seleccionada}
              onDelete={handleDelete}
              eliminando={eliminando === seleccionada.id}
            />
          ) : (
            <div className="text-sm text-slate-500 flex items-center gap-2">
              <Eye className="w-4 h-4" /> Selecciona una anotación.
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

function DetalleAnotacion({ anotacion, onDelete, eliminando }) {
  const a = anotacion;
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <span
          className={`text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded ${
            a.veredicto === "bueno"
              ? "bg-emerald-100 text-emerald-700"
              : "bg-red-100 text-red-700"
          }`}
        >
          {a.veredicto}
        </span>
        <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
          {a.fase}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onDelete(a.id)}
          disabled={eliminando}
          className="ml-auto gap-1 border-red-300 text-red-600 hover:bg-red-50"
        >
          {eliminando ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Trash2 className="w-3.5 h-3.5" />
          )}
          Eliminar
        </Button>
      </div>

      <div className="space-y-3 text-xs">
        <Bloque label="Prompt">
          <div className="bg-slate-50 border border-slate-200 rounded p-2 whitespace-pre-wrap">
            {a.prompt}
          </div>
        </Bloque>

        {a.nota ? (
          <Bloque label="Nota del anotador">
            <div className="bg-amber-50 border border-amber-200 rounded p-2 italic">
              {a.nota}
            </div>
          </Bloque>
        ) : null}

        {a.proyecto_integral ? (
          <Bloque label="Proyecto integral">
            <pre className="bg-slate-50 border border-slate-200 rounded p-2 text-[10px] overflow-x-auto">
              {JSON.stringify(a.proyecto_integral, null, 2)}
            </pre>
          </Bloque>
        ) : null}

        {(a.intenciones ?? []).length > 0 ? (
          <Bloque label={`Intenciones (${a.intenciones.length})`}>
            <ul className="space-y-1">
              {a.intenciones.map((i, idx) => (
                <li
                  key={`${i.area}-${idx}`}
                  className="border border-slate-200 rounded px-2 py-1"
                >
                  <div className="font-mono text-[10px] text-slate-500">
                    {i.area} · {i.jobcard_id ?? "-"}
                  </div>
                  <div className="text-slate-700">{i.query}</div>
                  <div className="text-[10px] text-slate-500">
                    {i.cantidad ?? "-"} {i.unidad ?? ""}
                  </div>
                </li>
              ))}
            </ul>
          </Bloque>
        ) : null}

        {(a.partidas_finales ?? []).length > 0 ? (
          <Bloque label={`Partidas finales (${a.partidas_finales.length})`}>
            <ul className="space-y-1">
              {a.partidas_finales.map((p, idx) => (
                <li
                  key={`${p.jobcard_id ?? idx}-${idx}`}
                  className="border border-slate-200 rounded px-2 py-1 flex items-start justify-between gap-2"
                >
                  <div className="min-w-0">
                    <div className="font-mono text-[10px] text-slate-500">
                      {p.jobcard_id ?? "-"} · {p.area ?? ""}
                    </div>
                    <div className="text-slate-700 text-[11px] line-clamp-2">
                      {p.concepto}
                    </div>
                  </div>
                  <div className="text-right shrink-0 tabular-nums">
                    <div className="text-[10px] text-slate-500">
                      {p.cantidad} {p.unidad}
                    </div>
                    <div className="text-[11px] font-bold text-slate-700">
                      {fmtEUR(
                        (Number(p.cantidad) || 0) *
                          (Number(p.precio_unitario) || 0),
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </Bloque>
        ) : null}

        <div className="text-[10px] text-slate-400 pt-2 border-t border-slate-200">
          id: <code className="font-mono">{a.id}</code> · vector_id:{" "}
          <code className="font-mono">{a.vector_id ?? "-"}</code>
        </div>
      </div>
    </div>
  );
}

function Bloque({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">
        {label}
      </div>
      {children}
    </div>
  );
}
